/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, DownloadIcon, SaveIcon, ShareIcon, SparklesIcon, CheckCircleIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { OutfitLayer, UserProfile, SavedContent } from '../types';
import { generateSocialMediaContent } from '../services/geminiService';
import { downloadImageFromDataUrl, getFriendlyErrorMessage } from '../lib/utils';
import Spinner from './Spinner';

interface ShareOutfitModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentOutfitImageUrl?: string;
  isLoading: boolean;
  onSaveContent: (content: Omit<SavedContent, 'id' | 'createdAt'>) => void;
  currentLayer: OutfitLayer;
  baseModelImageUrl: string | null;
  userProfile: UserProfile;
}

const ShareOutfitModal: React.FC<ShareOutfitModalProps> = ({
  isOpen,
  onClose,
  currentOutfitImageUrl,
  isLoading,
  onSaveContent,
  currentLayer,
  baseModelImageUrl,
  userProfile
}) => {
  const { t, lang } = useTranslation();
  const [description, setDescription] = useState('');
  const [socialContent, setSocialContent] = useState<{
    linkedin: { text: string; imageUrl: string | null };
    twitter: { text: string; imageUrl: string | null };
    instagram: { text: string; imageUrl: string | null };
  } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedStatus, setSavedStatus] = useState<Record<string, boolean>>({});

  const handleDownload = () => {
    if (currentOutfitImageUrl) {
      downloadImageFromDataUrl(currentOutfitImageUrl, t('shareOutfitModal_download_filename') + '.png');
    }
  };

  const handleGenerateContent = async () => {
    if (!currentOutfitImageUrl) {
        setError(t('shareOutfitModal_error_noImage'));
        return;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {
        const mainGarmentDesc = currentLayer.mainGarment ? t(currentLayer.mainGarment.nameKey) : '';
        const accessoriesDesc = currentLayer.accessories.map(a => t(a.nameKey)).join(', ');
        
        const outfitDescription = [mainGarmentDesc, accessoriesDesc].filter(Boolean).join(` ${t('social_with')} `);
        
        const poseDescription = currentLayer.currentPoseInstruction;
        const backgroundDescription = currentLayer.sceneConfig?.backgroundPrompt;

        const content = await generateSocialMediaContent(
            outfitDescription,
            currentOutfitImageUrl,
            poseDescription,
            backgroundDescription
        );
        setSocialContent(content);
    } catch (err) {
        setError(getFriendlyErrorMessage(err, t('shareOutfitModal_error_generationFailed'), lang, t));
    } finally {
        setIsGenerating(false);
    }
  };

  const handleSaveSocialContent = (platform: 'linkedin' | 'twitter' | 'instagram') => {
      if (!socialContent) return;
      
      const content = socialContent[platform];
      onSaveContent({
          type: 'social_media_post',
          title: t('shareOutfitModal_saveContentTitle', { itemName: currentLayer.mainGarment ? t(currentLayer.mainGarment.nameKey) : 'Outfit' }),
          content: content.text,
          [`${platform}Text`]: content.text,
          [`${platform}ImageUrl`]: content.imageUrl,
          sourceImageUrl: currentOutfitImageUrl || undefined
      });
      setSavedStatus(prev => ({ ...prev, [platform]: true }));
  };

  const handleShareApp = () => {
      if (navigator.share) {
          navigator.share({
              title: t('appName'),
              text: t('shareOutfitModal_shareText'),
              url: window.location.href,
          }).catch(console.error);
      } else {
          navigator.clipboard.writeText(window.location.href);
          alert(t('common_copied'));
      }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100">{t('shareOutfitModal_title')}</h2>
              <button onClick={onClose} className="p-1 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto custom-scrollbar flex-grow grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left: Image & Basic Actions */}
                <div className="flex flex-col gap-4">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden aspect-[3/4] relative shadow-md">
                        {currentOutfitImageUrl ? (
                            <img src={currentOutfitImageUrl} alt="Outfit" className="w-full h-full object-cover" />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-400">{t('retailSearchModal_noPreview')}</div>
                        )}
                    </div>
                    
                    <div className="flex gap-2">
                        <button onClick={handleDownload} disabled={!currentOutfitImageUrl} className="flex-1 flex items-center justify-center gap-2 bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50">
                            <DownloadIcon className="w-5 h-5" /> {t('shareOutfitModal_download')}
                        </button>
                        <button onClick={handleShareApp} className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white py-2.5 rounded-lg hover:bg-indigo-700 transition-colors">
                            <ShareIcon className="w-5 h-5" /> {t('shareOutfitModal_shareAppLink')}
                        </button>
                    </div>
                    
                    <textarea 
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder={t('shareOutfitModal_descriptionPlaceholder')}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-200 resize-none"
                        rows={3}
                    />
                </div>

                {/* Right: AI Content Generation */}
                <div className="flex flex-col h-full">
                    <div className="mb-4">
                        <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                            <SparklesIcon className="w-5 h-5 text-purple-500" />
                            {t('shareOutfitModal_aiContent_title')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('shareOutfitModal_aiContent_desc')}</p>
                    </div>

                    {!socialContent ? (
                        <div className="flex-grow flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-700/30 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 p-8">
                            {isGenerating ? (
                                <>
                                    <Spinner className="w-10 h-10 text-purple-500 mb-4" />
                                    <p className="text-gray-600 dark:text-gray-300 font-medium">{t('shareOutfitModal_generating')}</p>
                                </>
                            ) : error ? (
                                <div className="text-center text-red-500">
                                    <p>{error}</p>
                                    <button onClick={handleGenerateContent} className="mt-4 text-indigo-600 hover:underline">{t('startScreen_tryAgain')}</button>
                                </div>
                            ) : (
                                <button onClick={handleGenerateContent} className="flex flex-col items-center gap-2 text-gray-500 hover:text-purple-600 transition-colors">
                                    <SparklesIcon className="w-12 h-12" />
                                    <span className="font-semibold">{t('shareOutfitModal_generateButton')}</span>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex-grow overflow-y-auto custom-scrollbar space-y-4 pr-2">
                            {/* LinkedIn */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-bold text-[#0A66C2]">LinkedIn</h4>
                                    <button onClick={() => handleSaveSocialContent('linkedin')} disabled={savedStatus.linkedin} className="text-xs flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400">
                                        {savedStatus.linkedin ? <CheckCircleIcon className="w-4 h-4 text-green-500" /> : <SaveIcon className="w-4 h-4" />}
                                        {savedStatus.linkedin ? t('shareOutfitModal_contentSaved') : t('shareOutfitModal_saveContent')}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{socialContent.linkedin.text}</p>
                            </div>

                            {/* Twitter */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-bold text-[#1DA1F2]">X / Twitter</h4>
                                    <button onClick={() => handleSaveSocialContent('twitter')} disabled={savedStatus.twitter} className="text-xs flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400">
                                        {savedStatus.twitter ? <CheckCircleIcon className="w-4 h-4 text-green-500" /> : <SaveIcon className="w-4 h-4" />}
                                        {savedStatus.twitter ? t('shareOutfitModal_contentSaved') : t('shareOutfitModal_saveContent')}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{socialContent.twitter.text}</p>
                            </div>

                            {/* Instagram */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-bold text-[#E1306C]">Instagram</h4>
                                    <button onClick={() => handleSaveSocialContent('instagram')} disabled={savedStatus.instagram} className="text-xs flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400">
                                        {savedStatus.instagram ? <CheckCircleIcon className="w-4 h-4 text-green-500" /> : <SaveIcon className="w-4 h-4" />}
                                        {savedStatus.instagram ? t('shareOutfitModal_contentSaved') : t('shareOutfitModal_saveContent')}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{socialContent.instagram.text}</p>
                            </div>
                            
                            <button onClick={handleGenerateContent} className="w-full py-2 text-sm text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors">
                                {t('variationsModal_regenerate')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareOutfitModal;
